import { derived, get, writable, type Readable } from 'svelte/store'
import { notificationsApi } from '../api/notifications'
import { procurementsApi, type ProcurementSummary } from '../api/procurements'

export interface InAppNotification {
  id: string
  procurementId: number
  title: string
  publishedAt: string | null
  sourceName: string
  aiScore: number | null
  highRelevance: boolean
}

export interface SourceGroup {
  sourceName: string
  tenders: InAppNotification[]
}

interface NotificationState {
  lastVisitAt: string
  dismissed: Record<string, number>
}

const STATE_KEY = 'argus.notifications.state.v1'
const FETCH_LIMIT = 200

function initialState(): NotificationState {
  return { lastVisitAt: new Date().toISOString(), dismissed: {} }
}

function loadState(): NotificationState {
  if (typeof window === 'undefined') return initialState()
  try {
    const raw = window.localStorage.getItem(STATE_KEY)
    if (!raw) {
      const seed = initialState()
      saveState(seed)
      return seed
    }
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return initialState()
    const obj = parsed as Partial<NotificationState>
    return {
      lastVisitAt: typeof obj.lastVisitAt === 'string' ? obj.lastVisitAt : new Date().toISOString(),
      dismissed: obj.dismissed && typeof obj.dismissed === 'object' ? obj.dismissed : {},
    }
  } catch {
    return initialState()
  }
}

function saveState(state: NotificationState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(state))
  } catch {
    // Storage unavailable — degrade to in-memory only.
  }
}

function isHighRelevance(p: ProcurementSummary, minAiScore: number | null): boolean {
  return minAiScore !== null && p.aiScore !== null && p.aiScore >= minAiScore
}

function toNotification(p: ProcurementSummary, minAiScore: number | null): InAppNotification {
  const highRelevance = isHighRelevance(p, minAiScore)
  return {
    // A score arriving after a regular notification was dismissed must create
    // a new, independently dismissible high-relevance alert.
    id: highRelevance ? `proc-${p.id}-high-${p.aiScore}` : `proc-${p.id}`,
    procurementId: p.id,
    title: p.title,
    publishedAt: p.publishedAt,
    sourceName: p.sourceName,
    aiScore: p.aiScore,
    highRelevance,
  }
}

const notificationsStore = writable<InAppNotification[]>([])
const stateStore = writable<NotificationState>(loadState())
const loadingStore = writable<boolean>(false)
const errorStore = writable<string | null>(null)

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STATE_KEY) return
    stateStore.set(loadState())
  })
}

let activeFetchController: AbortController | null = null
let refreshQueued = false

// AI batch polling can report several completed tenders in the same event-loop
// turn. Coalesce those callbacks into one immediate notification reload instead
// of starting (and aborting) one request per tender.
export function requestNotificationRefresh(): void {
  if (refreshQueued) return
  refreshQueued = true
  queueMicrotask(() => {
    refreshQueued = false
    void loadNotifications()
  })
}

export async function loadNotifications(): Promise<void> {
  activeFetchController?.abort()
  const controller = new AbortController()
  activeFetchController = controller

  loadingStore.set(true)
  errorStore.set(null)
  try {
    const since = get(stateStore).lastVisitAt
    const [config, items] = await Promise.all([
      notificationsApi.getConfig({ signal: controller.signal }),
      procurementsApi.list(
        { notificationSince: since, limit: FETCH_LIMIT },
        { signal: controller.signal },
      ),
    ])
    if (controller.signal.aborted) return
    notificationsStore.set(
      config.enabled
        ? items
            .filter(
              (item) =>
                isHighRelevance(item, config.minAiScore) ||
                ((item.procurementType !== 'simple' || config.notifySimpleProcurement) &&
                  (item.procurementType !== 'public' || config.notifyPublicProcurement)),
            )
            .map((item) => toNotification(item, config.minAiScore))
        : [],
    )
  } catch (err) {
    if (controller.signal.aborted) return
    errorStore.set(err instanceof Error ? err.message : String(err))
  } finally {
    if (!controller.signal.aborted) loadingStore.set(false)
    if (activeFetchController === controller) activeFetchController = null
  }
}

function mutateState(mutator: (state: NotificationState) => NotificationState): void {
  const next = mutator(get(stateStore))
  stateStore.set(next)
  saveState(next)
}

export function dismiss(id: string): void {
  mutateState((s) => {
    if (s.dismissed[id]) return s
    return { ...s, dismissed: { ...s.dismissed, [id]: Date.now() } }
  })
}

export function dismissGroup(ids: string[]): void {
  mutateState((s) => {
    const dismissed = { ...s.dismissed }
    const dismissedAt = Date.now()
    for (const id of ids) dismissed[id] = dismissedAt
    return { ...s, dismissed }
  })
}

export function markAllRead(): void {
  mutateState(() => ({ lastVisitAt: new Date().toISOString(), dismissed: {} }))
  notificationsStore.set([])
  void loadNotifications()
}

export const visibleNotifications: Readable<InAppNotification[]> = derived(
  [notificationsStore, stateStore],
  ([$notifications, $state]) => $notifications.filter((n) => !$state.dismissed[n.id]),
)

// High-relevance tenders are always surfaced as individual notifications.
export const highRelevanceNotifications: Readable<InAppNotification[]> = derived(
  visibleNotifications,
  ($visible) => $visible.filter((notification) => notification.highRelevance),
)

// Regular notifications retain the existing one-entry-per-source presentation.
export const groupedNotifications: Readable<SourceGroup[]> = derived(
  visibleNotifications,
  ($visible) => {
    const map = new Map<string, InAppNotification[]>()
    for (const n of $visible) {
      if (n.highRelevance) continue
      const existing = map.get(n.sourceName)
      if (existing) {
        existing.push(n)
      } else {
        map.set(n.sourceName, [n])
      }
    }
    return Array.from(map.entries()).map(([sourceName, tenders]) => ({ sourceName, tenders }))
  },
)

// Badge count: each high-relevance tender plus each regular source group.
export const unreadCount: Readable<number> = derived(
  [highRelevanceNotifications, groupedNotifications],
  ([$highRelevance, $groups]) => $highRelevance.length + $groups.length,
)

export const notificationsLoading: Readable<boolean> = { subscribe: loadingStore.subscribe }
export const notificationsError: Readable<string | null> = { subscribe: errorStore.subscribe }
